import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const SCROLL_LINKED_DIRECTION_FLOOR_PX = 6;
export const SCROLL_LINKED_COOLDOWN_MS = 300;

export interface ScrollLinkedSectionsOptions<K extends string> {
    sectionKeys: readonly K[];
    expandedKeys: readonly K[];
    onToggle: (key: K, expand: boolean) => void;
    enabled?: boolean;
    directionFloorPx?: number;
    cooldownMs?: number;
}

export interface ScrollLinkedSections<K extends string> {
    scrollContainerRef: (element: HTMLElement | null) => void;
    sectionRef: (key: K) => (element: HTMLElement | null) => void;
    markUserToggled: (key: K) => void;
    getSectionElement: (key: K) => HTMLElement | null;
}

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Scrolling down inside a bounded scroll container expands a collapsed section once that section is
 * in view; scrolling back up collapses the last one this hook expanded. State stays with the caller
 * — the hook only decides WHEN to ask for a toggle.
 *
 * **The trigger is visibility, not scrolled distance, and it must stay that way.** An earlier
 * version required a cumulative 24px of same-direction scroll before it would fire. That silently
 * assumes a tall container: on a six-row table the whole scrollable range is ~36px, so the gate
 * demanded two thirds of every pixel available in one uninterrupted gesture and in practice never
 * fired at all. Visibility has no such scale dependency — a section that has come into view is
 * exactly the section the reader is arriving at, on a 386px table and on a 7830px one alike. The
 * scroll delta now supplies only the direction sign, past a floor that rejects sub-pixel jitter.
 *
 * Because a section can already be in view at rest, visibility alone is not enough — expansion also
 * needs a deliberate downward gesture, which is what keeps the hook from expanding on mount.
 *
 * Three guards keep it from oscillating, and removing any one of them brings the oscillation back:
 *   - **Scroll anchoring.** Expanding inserts rows into the flow, so anything under the scroll
 *     position shifts; the scrollHeight delta goes back into scrollTop before paint, clamped to the
 *     range the container actually has. Content added BELOW the scroll position moves nothing on
 *     screen and is deliberately not compensated, which would otherwise teleport the viewport.
 *   - **A re-entry cooldown.** The anchoring write is itself a scroll event; without the cooldown
 *     it clears the direction floor again and toggles the next section immediately. The cooldown is
 *     re-armed from the moment of that write, so our own adjustment can never read as user intent.
 *   - **A direction floor.** Inertia tails and sub-pixel jitter would otherwise fire a toggle, and
 *     the pending delta resets on every direction change so opposing jitter cannot accumulate.
 *
 * A section the user toggles by hand (via `markUserToggled`) is left alone until it has scrolled
 * out of the container and back in. Auto-toggling stands down entirely under
 * `prefers-reduced-motion`. The hook never moves focus, and never touches ARIA — expanded state is
 * the caller's to render.
 */
export function useScrollLinkedSections<K extends string>({
    sectionKeys,
    expandedKeys,
    onToggle,
    enabled = true,
    directionFloorPx = SCROLL_LINKED_DIRECTION_FLOOR_PX,
    cooldownMs = SCROLL_LINKED_COOLDOWN_MS,
}: ScrollLinkedSectionsOptions<K>): ScrollLinkedSections<K> {
    const [container, setContainer] = useState<HTMLElement | null>(null);
    const active = enabled && !prefersReducedMotion();

    const sectionKeysRef = useRef(sectionKeys);
    sectionKeysRef.current = sectionKeys;
    const expandedRef = useRef<Set<K>>(new Set(expandedKeys));
    expandedRef.current = new Set(expandedKeys);
    const onToggleRef = useRef(onToggle);
    onToggleRef.current = onToggle;

    const sectionElements = useRef(new Map<K, HTMLElement>());
    const sectionRefs = useRef(new Map<K, (element: HTMLElement | null) => void>());

    const userToggled = useRef(new Set<K>());
    const leftContainer = useRef(new Set<K>());
    const inView = useRef(new Set<K>());
    const autoExpanded = useRef<K[]>([]);

    const lastScrollTop = useRef(0);
    const pendingDelta = useRef(0);
    const cooldownUntil = useRef(0);
    const pendingAnchor = useRef<{ scrollHeight: number; compensate: boolean } | null>(null);

    const sectionRef = useCallback((key: K) => {
        if (!sectionRefs.current.has(key)) {
            sectionRefs.current.set(key, (element) => {
                if (element) sectionElements.current.set(key, element);
                else sectionElements.current.delete(key);
            });
        }
        return sectionRefs.current.get(key) as (element: HTMLElement | null) => void;
    }, []);

    const markUserToggled = useCallback((key: K) => {
        userToggled.current.add(key);
        leftContainer.current.delete(key);
        autoExpanded.current = autoExpanded.current.filter((candidate) => candidate !== key);
    }, []);

    const getSectionElement = useCallback((key: K) => sectionElements.current.get(key) ?? null, []);

    const pickSection = useCallback((scrollingDown: boolean): K | null => {
        if (scrollingDown) {
            return (
                sectionKeysRef.current.find(
                    (key) => inView.current.has(key) && !expandedRef.current.has(key) && !userToggled.current.has(key),
                ) ?? null
            );
        }
        return [...autoExpanded.current].reverse().find((key) => expandedRef.current.has(key) && !userToggled.current.has(key)) ?? null;
    }, []);

    const toggle = useCallback(
        (key: K, expand: boolean) => {
            if (!container) return;
            const section = sectionElements.current.get(key);
            pendingAnchor.current = {
                scrollHeight: container.scrollHeight,
                compensate: !!section && section.offsetTop < container.scrollTop,
            };
            cooldownUntil.current = Date.now() + cooldownMs;
            autoExpanded.current = expand ? [...autoExpanded.current, key] : autoExpanded.current.filter((candidate) => candidate !== key);
            onToggleRef.current(key, expand);
        },
        [container, cooldownMs],
    );

    useEffect(() => {
        if (!active || !container) return undefined;

        lastScrollTop.current = container.scrollTop;
        let frame = 0;

        const evaluate = () => {
            frame = 0;
            const scrollTop = container.scrollTop;
            const delta = scrollTop - lastScrollTop.current;
            lastScrollTop.current = scrollTop;
            if (!delta) return;

            if (delta > 0 !== pendingDelta.current > 0) pendingDelta.current = 0;
            pendingDelta.current += delta;
            if (Date.now() < cooldownUntil.current || Math.abs(pendingDelta.current) < directionFloorPx) return;

            const scrollingDown = pendingDelta.current > 0;
            pendingDelta.current = 0;
            const key = pickSection(scrollingDown);
            if (key) toggle(key, scrollingDown);
        };

        const onScroll = () => {
            if (!frame) frame = requestAnimationFrame(evaluate);
        };

        container.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            container.removeEventListener('scroll', onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [active, container, directionFloorPx, pickSection, toggle]);

    useLayoutEffect(() => {
        const anchor = pendingAnchor.current;
        pendingAnchor.current = null;
        if (!anchor || !container) return;
        if (anchor.compensate) {
            const growth = container.scrollHeight - anchor.scrollHeight;
            const range = Math.max(0, container.scrollHeight - container.clientHeight);
            const target = Math.min(Math.max(container.scrollTop + growth, 0), range);
            if (range > 0 && Math.abs(growth) <= range && target !== container.scrollTop) {
                container.scrollTop = target;
                cooldownUntil.current = Date.now() + cooldownMs;
            }
        }
        lastScrollTop.current = container.scrollTop;
        pendingDelta.current = 0;
    }, [container, cooldownMs, expandedKeys]);

    useEffect(() => {
        if (!active || !container || typeof IntersectionObserver === 'undefined') return undefined;

        const observed = Array.from(sectionElements.current.entries());
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const key = observed.find(([, element]) => element === entry.target)?.[0];
                    if (!key) return;
                    if (!entry.isIntersecting) {
                        inView.current.delete(key);
                        leftContainer.current.add(key);
                        return;
                    }
                    inView.current.add(key);
                    if (leftContainer.current.delete(key)) userToggled.current.delete(key);
                });
            },
            { root: container },
        );
        observed.forEach(([, element]) => observer.observe(element));
        return () => observer.disconnect();
    }, [active, container, sectionKeys]);

    return { scrollContainerRef: setContainer, sectionRef, markUserToggled, getSectionElement };
}
